import { useConversationShow } from '@jobilla/nexus-sdk'
import type { UIMessage } from 'ai'
import { useMemo } from 'react'

function isNotFoundError(error: unknown): boolean {
	const err = error as { status?: number; response?: { status?: number } } | null
	return err?.status === 404 || err?.response?.status === 404
}

export function useConversationHistory(conversationId: string | null, isNew = false) {
	const { data, isLoading, isPending, error, isError } = useConversationShow(
		conversationId ?? '',
		{
			enabled: Boolean(conversationId) && !isNew,
			staleTime: Infinity,
			refetchOnMount: 'always',
			retry: (failureCount, error) => {
				if (isNotFoundError(error)) { return false }
				return failureCount < 3
			},
		},
	)

	const messages = useMemo<UIMessage[]>(() => {
		if (isError && isNotFoundError(error)) {
			return []
		}

		if (!data?.messages) {
			return []
		}

		// Parts are AI-SDK message parts persisted as opaque JSON — a `text` part
		// plus one `tool-<name>` part per tool call, so the union cannot be
		// expressed in the spec and is reconstructed here rather than validated.
		return data.messages.map((raw) => ({
			id: raw.id,
			role: raw.role as UIMessage['role'],
			parts: raw.parts as UIMessage['parts'],
		}))
	}, [data, isError, error])

	return {
		messages,
		isLoadingHistory: !isNew && (isLoading || isPending),
	}
}
